import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Tooltip from "../../ui/Tooltip";
import type { AppCategory, AppItem } from "../AppsSidebar/types";
import { useFavoriteApps } from "./useFavoriteApps";
import { resolveFavorites } from "./favoriteApps";
import { appTileClass } from "../AppsSidebar/appTile";
import { useInRouterContext } from "react-router-dom";
import RailMenu, { type RailMenuTriggerProps } from "./RailMenu";
import { reportMenuEntries } from "./reportEntries";

interface AppsRailProps {
  categories: AppCategory[];
  currentAppId?: string;
  onAppSelect: (app: AppItem) => void;
  isDarkMode: boolean;
}

// Riel vertical de apps favoritas, a la izquierda, al estilo de Mapon: las
// apps que el usuario usa están a un clic y siempre a la vista. El menú
// completo se abre con el botón de apps junto al avatar. Antes, para pasar
// del mapa a Reportes había que adivinar que las apps estaban detrás del
// avatar, abrir el panel, desplegar la categoría y tocar la app.
//
// `categories` es la lista YA filtrada por permisos (useFilteredCategories),
// la misma del menú, y la navegación usa el mismo onAppSelect del layout: el
// riel no puede mostrar ni abrir algo que el menú no permitiría.
export default function AppsRail({ categories, currentAppId, onAppSelect, isDarkMode }: AppsRailProps) {
  const { t } = useTranslation("common");
  const { favorites } = useFavoriteApps();
  const apps = useMemo(
    () => resolveFavorites(favorites, categories.flatMap((c) => c.apps)),
    [favorites, categories],
  );

  // Apps que acaban de entrar a la barra: se animan al aparecer (railIn +
  // anillo ámbar) para que quien tocó la estrella vea a dónde fue el acceso.
  // La primera carga no anima nada.
  const prev = useRef<string[] | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (prev.current) {
      const added = favorites.filter((id) => !prev.current!.includes(id));
      if (added.length) {
        setFresh(new Set(added));
        const id = setTimeout(() => setFresh(new Set()), 1600);
        prev.current = favorites;
        return () => clearTimeout(id);
      }
    }
    prev.current = favorites;
  }, [favorites]);

  // Informes abre un menú con sus variantes en vez de ir directo a una ruta
  // (como en Mapon). Sólo en el riel: el menú completo de apps no cambia. Fuera
  // de un Router (tests del menú) el acceso se comporta como cualquier otro.
  const inRouter = useInRouterContext();
  const reportEntries = reportMenuEntries(t);

  const railBg = isDarkMode ? "bg-dark-bg border-dark-border" : "bg-[#f5f6f2] border-[#e3e6de]";

  // Reserva el ancho de la barra en TODA la app (escritorio): la barra es fija
  // y los layouts (34 montajes del menú, cada uno con su h-screen) no saben de
  // ella. index.css aplica padding-left a #root mientras exista esta clase.
  useEffect(() => {
    document.documentElement.classList.add("has-apps-rail");
    return () => document.documentElement.classList.remove("has-apps-rail");
  }, []);

  return createPortal(
    <nav
      aria-label={t("appsRail.label", "Barra de accesos rápidos")}
      className={`fixed left-0 top-0 bottom-0 z-30 w-14 hidden lg:flex flex-col items-center gap-1 py-3 border-r ${railBg}`}
    >
      {apps.map((app) => {
        const active = app.id === currentAppId;
        const withMenu = inRouter && app.id === "reportes" && !app.disabled;
        const tile = (extra: Partial<RailMenuTriggerProps> = {}) => (
          <button
            type="button"
            aria-label={app.name}
            aria-current={active ? "page" : undefined}
            disabled={app.disabled}
            onClick={() => onAppSelect(app)}
            {...extra}
            className="group relative w-11 h-11 flex items-center justify-center rounded-xl active:scale-95 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {fresh.has(app.id) && (
              <span aria-hidden className="absolute inset-1 rounded-xl bg-amber-400/50 motion-safe:animate-railRing opacity-0" />
            )}
            {/* Marca de la app actual: barra a la izquierda, como en Mapon. */}
            {active && <span aria-hidden className="absolute -left-1.5 top-2.5 bottom-2.5 w-[3px] rounded-r bg-primary-500" />}
            <span
              className={`${appTileClass(app.disabled ? "disabled" : active ? "active" : "idle", isDarkMode)} relative w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-colors duration-200 ${
                fresh.has(app.id) ? "motion-safe:animate-railIn" : ""
              }`}
            >
              {app.icon}
            </span>
          </button>
        );
        // Con menú no lleva tooltip: el propio menú ya dice qué es.
        return withMenu ? (
          <RailMenu
            key={app.id}
            label={app.name}
            entries={reportEntries}
            isDarkMode={isDarkMode}
            sameApp={active}
            onLeaveApp={(route) => onAppSelect({ ...app, route })}
          >
            {(trigger) => tile(trigger)}
          </RailMenu>
        ) : (
          <Tooltip key={app.id} content={app.name} position="right" delay={150} className="!w-auto">
            {tile()}
          </Tooltip>
        );
      })}
    </nav>,
    document.body,
  );
}
